import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { installationsAPI, engineersAPI, customersAPI } from '../lib/api';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  User,
  MapPin,
  Loader2,
  MoreHorizontal,
  Edit,
  Trash2,
  Wrench,
  Zap,
  Sun,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const INSTALLATION_TYPES = [
  { value: 'solar', label: 'Solar Installation', icon: Sun },
  { value: 'electrical', label: 'Electrical Work', icon: Zap },
  { value: 'both', label: 'Solar + Electrical', icon: Wrench },
  { value: 'inspection', label: 'Inspection', icon: Eye },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench },
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

// Import an Outlook event from the hello@ shared calendar into a CRM Installation.
// Lets admin pick a customer + (optional) engineers and pre-fills date/duration
// from the event payload.
const ImportOutlookEventDialog = ({ event, customers, engineers, onClose, onImported }) => {
  const [customerId, setCustomerId] = useState('');
  const [engineerIds, setEngineerIds] = useState([]);
  const [installationType, setInstallationType] = useState('solar');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setCustomerId('');
    setEngineerIds([]);
    setInstallationType('solar');
    setSearchQuery('');
  }, [event?.id]);

  if (!event) return null;

  const filteredCustomers = (customers || []).filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.address || '').toLowerCase().includes(q);
  }).slice(0, 50);

  const handleImport = async () => {
    if (!customerId) {
      toast.error('Pick a customer first');
      return;
    }
    setSaving(true);
    try {
      const startDate = event.start_date || event.start?.slice(0, 10);
      const endDate = event.end_date || startDate;
      const startTime = event.start?.slice(11, 16) || '09:00';
      const endTime = event.end?.slice(11, 16) || '17:00';
      await installationsAPI.create({
        customer_id: customerId,
        engineer_ids: engineerIds,
        engineer_id: engineerIds[0] || '',
        title: event.subject || 'Imported from Outlook',
        description: `Imported from Outlook (${event.organizer || event.mailbox || 'hello@'})${event.location ? ' · ' + event.location : ''}`,
        scheduled_date: startDate,
        end_date: endDate,
        start_time: startTime,
        end_time: endTime,
        installation_type: installationType,
        status: 'scheduled',
        outlook_event_id: event.id,
        outlook_owner: event.mailbox || 'hello@helioses.co.uk',
      });
      toast.success('Imported from Outlook — installation created');
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="import-outlook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-lg">📥</span>
            Import Outlook event
          </DialogTitle>
          <DialogDescription>
            Link this Outlook booking to a CRM customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded text-sm space-y-1">
            <p><strong>{event.subject || '(no subject)'}</strong></p>
            <p className="text-xs text-slate-600">
              {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` → ${event.end_date}` : ''}
              {event.location ? ` · ${event.location}` : ''}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Customer</label>
            <Input
              placeholder="Search by name, email or address…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mt-1"
              data-testid="import-customer-search"
            />
            <div className="mt-2 border border-slate-200 rounded max-h-48 overflow-y-auto">
              {filteredCustomers.length === 0 ? (
                <p className="text-xs text-slate-400 p-3 text-center">No matches</p>
              ) : (
                filteredCustomers.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setCustomerId(c.id)}
                    className={`block w-full text-left text-xs px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${customerId === c.id ? 'bg-amber-50 text-amber-900' : ''}`}
                    data-testid={`import-customer-${c.id}`}
                  >
                    <strong>{c.first_name} {c.last_name}</strong>
                    <span className="text-slate-500 ml-2">{c.email}</span>
                    {c.address && <span className="block text-[10px] text-slate-400">{c.address}{c.postcode ? `, ${c.postcode}` : ''}</span>}
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Type</label>
              <Select value={installationType} onValueChange={setInstallationType}>
                <SelectTrigger className="mt-1" data-testid="import-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTALLATION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Engineer (optional)</label>
              <Select value={engineerIds[0] || ''} onValueChange={(v) => setEngineerIds(v ? [v] : [])}>
                <SelectTrigger className="mt-1" data-testid="import-engineer">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  {(engineers || []).filter(e => e.active !== false).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-import">
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={saving || !customerId}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="confirm-import"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [installations, setInstallations] = useState([]);
  const [ms365Events, setMs365Events] = useState([]);
  const [showMs365, setShowMs365] = useState(true);
  const [engineers, setEngineers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [importOutlookEvent, setImportOutlookEvent] = useState(null);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [draggedInstallation, setDraggedInstallation] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    engineer_ids: [],
    title: '',
    description: '',
    scheduled_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    installation_type: 'solar',
  });
  const [saving, setSaving] = useState(false);
  const [filterEngineer, setFilterEngineer] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'swimlane'
  const [swimlaneWeekStart, setSwimlaneWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday of this week
    return d.toISOString().slice(0, 10);
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${getDaysInMonth(year, month)}`;
      const [installationsRes, engineersRes, customersRes, ms365Res] = await Promise.all([
        installationsAPI.getMonth(year, month + 1),
        engineersAPI.getAll(),
        customersAPI.getAll(),
        // Pull MS365 events from hello@ shared calendar so Outlook bookings
        // (and any auto-created install events) appear alongside CRM installations.
        api.get(`/calendar/ms365-events?mailbox=hello@helioses.co.uk&start_date=${monthStart}&end_date=${monthEnd}`).catch(() => ({ data: { events: [], configured: false } })),
      ]);
      setInstallations(installationsRes.data.installations || []);
      setEngineers(engineersRes.data);
      setCustomers(customersRes.data);
      setMs365Events(ms365Res.data?.events || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year, month]);

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get installations for a specific date — matches every day in the
  // [scheduled_date..end_date] range so multi-day installs render across
  // the full span instead of only the first day.
  const getInstallationsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    let filtered = installations.filter(inst => {
      const start = inst.scheduled_date;
      const end = inst.end_date || inst.scheduled_date;
      return start <= dateStr && dateStr <= end;
    });
    if (filterEngineer !== 'all') {
      filtered = filtered.filter(inst => inst.engineer_id === filterEngineer || (inst.engineer_ids || []).includes(filterEngineer));
    }
    return filtered;
  };

  // MS365 (hello@) events for a specific day — pulled from Outlook
  const getMs365EventsForDate = (day) => {
    if (!showMs365 || !ms365Events.length) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return ms365Events.filter(ev => {
      const start = ev.start_date;
      const end = ev.end_date || ev.start_date;
      return start && start <= dateStr && dateStr <= end;
    });
  };

  // Dialog handlers
  const openNewDialog = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setEditingInstallation(null);
    setFormData({
      customer_id: '',
      engineer_ids: engineers[0]?.id ? [engineers[0].id] : [],
      title: '',
      description: '',
      scheduled_date: dateStr,
      end_date: dateStr,
      start_time: '09:00',
      end_time: '17:00',
      installation_type: 'solar',
    });
    setShowDialog(true);
  };

  const openEditDialog = (installation) => {
    setEditingInstallation(installation);
    setFormData({
      customer_id: installation.customer_id,
      engineer_ids: installation.engineer_ids?.length ? installation.engineer_ids : [installation.engineer_id],
      title: installation.title,
      description: installation.description,
      scheduled_date: installation.scheduled_date,
      end_date: installation.end_date || installation.scheduled_date,
      start_time: installation.start_time,
      end_time: installation.end_time,
      installation_type: installation.installation_type,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.customer_id || formData.engineer_ids.length === 0 || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        end_date: formData.end_date || formData.scheduled_date,
      };
      
      if (editingInstallation) {
        await installationsAPI.update(editingInstallation.id, submitData);
        toast.success('Installation updated');
      } else {
        await installationsAPI.create(submitData);
        toast.success('Installation scheduled');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this installation?')) return;
    try {
      await installationsAPI.delete(id);
      toast.success('Installation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await installationsAPI.update(id, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, installation) => {
    setDraggedInstallation(installation);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', installation.id);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedInstallation(null);
    setDragOverDay(null);
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    setDragOverDay(null);
    
    if (!draggedInstallation) return;
    
    const newDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Don't update if dropping on the same day
    if (draggedInstallation.scheduled_date === newDateStr) {
      setDraggedInstallation(null);
      return;
    }
    
    // Calculate the difference in days if it's a multi-day event
    let newEndDate = newDateStr;
    if (draggedInstallation.end_date && draggedInstallation.end_date !== draggedInstallation.scheduled_date) {
      const startDate = new Date(draggedInstallation.scheduled_date);
      const endDate = new Date(draggedInstallation.end_date);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      const newStart = new Date(newDateStr);
      const newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + daysDiff);
      newEndDate = newEnd.toISOString().split('T')[0];
    }
    
    try {
      await installationsAPI.update(draggedInstallation.id, {
        scheduled_date: newDateStr,
        end_date: newEndDate,
      });
      toast.success(`Rescheduled to ${formatDate(newDateStr)}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule installation');
    }
    
    setDraggedInstallation(null);
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isToday = (day) => 
    today.getDate() === day && 
    today.getMonth() === month && 
    today.getFullYear() === year;

  const calendarDays = [];
  // Empty cells for days before the first day of month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Installation Calendar</h1>
            <p className="text-slate-500 mt-1">Schedule and manage installations • Drag events to reschedule</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid="view-grid"
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setViewMode('swimlane')}
                className={`px-3 py-1.5 text-sm border-l border-slate-200 ${viewMode === 'swimlane' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid="view-swimlane"
              >
                Engineer Week
              </button>
            </div>
            <Select value={filterEngineer} onValueChange={setFilterEngineer}>
              <SelectTrigger className="w-[200px]" data-testid="filter-engineer">
                <User className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Engineers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Engineers</SelectItem>
                {engineers.map((eng) => (
                  <SelectItem key={eng.id} value={eng.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: eng.color }}
                      />
                      {eng.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => setShowMs365(v => !v)}
              className={`px-3 py-1.5 text-sm rounded-md border ${showMs365 ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              title={showMs365 ? 'Hiding Outlook events from hello@helioses.co.uk' : 'Showing Outlook events from hello@helioses.co.uk'}
              data-testid="toggle-ms365-events"
            >
              <span className="mr-1">📅</span>Outlook ({ms365Events.length})
            </button>
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
              title="Force-refresh installations + Outlook events"
              data-testid="sync-now-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Sync now
            </button>
          </div>
        </div>

        {/* Calendar Navigation */}
        {viewMode === 'grid' ? (
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="icon" onClick={goToPreviousMonth} data-testid="prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-xl font-semibold text-slate-900 font-['Manrope'] min-w-[200px] text-center">
                  {MONTHS[month]} {year}
                </h2>
                <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline" onClick={goToToday} data-testid="today-btn">
                Today
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((day) => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="min-h-[120px] bg-slate-50/50 rounded-lg" />;
                }

                const dayInstallations = getInstallationsForDate(day);
                const hasInstallations = dayInstallations.length > 0;
                const isDragOver = dragOverDay === day;

                return (
                  <div
                    key={day}
                    className={`min-h-[120px] p-2 rounded-lg border transition-all cursor-pointer ${
                      isToday(day)
                        ? 'border-amber-400 bg-amber-50'
                        : isDragOver
                        ? 'border-blue-400 bg-blue-50 border-dashed border-2'
                        : 'border-slate-200 hover:border-amber-300 bg-white'
                    }`}
                    onClick={() => openNewDialog(day)}
                    onDragOver={(e) => handleDragOver(e, day)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, day)}
                    data-testid={`calendar-day-${day}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${isToday(day) ? 'text-amber-600' : 'text-slate-700'}`}>
                        {day}
                      </span>
                      {hasInstallations && (
                        <Badge className="text-xs px-1 py-0 bg-amber-100 text-amber-700">
                          {dayInstallations.length}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1 max-h-[80px] overflow-y-auto">
                      {dayInstallations.slice(0, 3).map((inst) => (
                        <div
                          key={inst.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, inst)}
                          onDragEnd={handleDragEnd}
                          className="text-xs p-1 rounded truncate cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                          style={{ backgroundColor: inst.engineer_color + '20', borderLeft: `3px solid ${inst.engineer_color}` }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(inst);
                          }}
                        >
                          <div className="font-medium truncate">{inst.title}</div>
                          <div className="text-slate-500 truncate">{inst.start_time} - {inst.engineer_name}</div>
                        </div>
                      ))}
                      {dayInstallations.length > 3 && (
                        <div className="text-xs text-slate-500 text-center">
                          +{dayInstallations.length - 3} more
                        </div>
                      )}
                      {/* MS365 events from hello@ shared calendar */}
                      {showMs365 && getMs365EventsForDate(day).slice(0, 2).map((ev) => (
                        <div
                          key={ev.id}
                          className="group text-xs p-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded truncate flex items-center gap-1 hover:bg-indigo-100"
                          title={`${ev.subject}${ev.location ? ' · ' + ev.location : ''} (Outlook)`}
                          data-testid={`ms365-event-${ev.id}`}
                        >
                          <span className="text-[10px]">📅</span>
                          <span
                            className="flex-1 truncate cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); if (ev.web_link) window.open(ev.web_link, '_blank'); }}
                          >
                            {ev.subject}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setImportOutlookEvent(ev); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-indigo-700 hover:text-indigo-900 hover:bg-indigo-200 rounded px-1"
                            title="Import as CRM Installation"
                            data-testid={`import-ms365-${ev.id}`}
                          >
                            ↓
                          </button>
                        </div>
                      ))}
                      {showMs365 && getMs365EventsForDate(day).length > 2 && (
                        <div className="text-[10px] text-indigo-500 text-center">
                          +{getMs365EventsForDate(day).length - 2} from Outlook
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        ) : (
        <Card className="border-slate-200" data-testid="swimlane-view">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => {
                  const d = new Date(swimlaneWeekStart);
                  d.setDate(d.getDate() - 7);
                  setSwimlaneWeekStart(d.toISOString().slice(0, 10));
                }} data-testid="swimlane-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <h2 className="text-base font-semibold min-w-[200px] text-center">
                  Week of {new Date(swimlaneWeekStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </h2>
                <Button variant="outline" size="icon" onClick={() => {
                  const d = new Date(swimlaneWeekStart);
                  d.setDate(d.getDate() + 7);
                  setSwimlaneWeekStart(d.toISOString().slice(0, 10));
                }} data-testid="swimlane-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <Button variant="outline" onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - d.getDay() + 1);
                setSwimlaneWeekStart(d.toISOString().slice(0, 10));
              }}>This week</Button>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Build the 7 weekdays starting from swimlaneWeekStart
              const days = [];
              for (let i = 0; i < 7; i++) {
                const d = new Date(swimlaneWeekStart);
                d.setDate(d.getDate() + i);
                days.push(d);
              }
              // Filter engineers if a single one is selected
              const enginesToShow = filterEngineer === 'all'
                ? engineers
                : engineers.filter((e) => e.id === filterEngineer);
              const findInstalls = (engId, dateStr) =>
                installations.filter((i) => {
                  const idsArr = (i.engineer_ids && i.engineer_ids.length > 0) ? i.engineer_ids : (i.engineer_id ? [i.engineer_id] : []);
                  if (!idsArr.includes(engId)) return false;
                  const start = i.scheduled_date;
                  const end = i.end_date || i.scheduled_date;
                  return start <= dateStr && dateStr <= end;
                });
              return (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr>
                        <th className="text-left text-xs uppercase text-slate-500 px-2 py-2 sticky left-0 bg-white">Engineer</th>
                        {days.map((d) => (
                          <th key={d.toISOString()} className="text-center text-xs uppercase text-slate-500 px-1 py-2 min-w-[110px]">
                            {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                            <div className="text-slate-400 font-normal">{d.getDate()}/{d.getMonth() + 1}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {enginesToShow.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-6 text-slate-400">No engineers</td></tr>
                      ) : enginesToShow.map((eng) => (
                        <tr key={eng.id} className="border-t border-slate-100" data-testid={`swimlane-row-${eng.id}`}>
                          <td className="px-2 py-2 sticky left-0 bg-white">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: eng.color }}></div>
                              <span className="text-sm font-medium truncate">{eng.name}</span>
                            </div>
                          </td>
                          {days.map((d) => {
                            const dateStr = d.toISOString().slice(0, 10);
                            const dayInstalls = findInstalls(eng.id, dateStr);
                            return (
                              <td key={dateStr} className="px-1 py-1 align-top border-l border-slate-100 min-h-[60px]">
                                {dayInstalls.length === 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => openNewDialog(d.getDate())}
                                    className="w-full h-12 rounded text-[10px] text-slate-300 hover:bg-slate-50 hover:text-slate-500 transition"
                                  >
                                    +
                                  </button>
                                ) : (
                                  <div className="space-y-1">
                                    {dayInstalls.map((inst) => (
                                      <button
                                        key={inst.id}
                                        type="button"
                                        onClick={() => openEditDialog(inst)}
                                        className="block w-full text-left text-[11px] p-1.5 rounded hover:shadow"
                                        style={{ backgroundColor: (eng.color || '#3b82f6') + '20', borderLeft: `3px solid ${eng.color || '#3b82f6'}` }}
                                        data-testid={`swimlane-inst-${inst.id}`}
                                      >
                                        <div className="font-medium truncate">{inst.customer_name || inst.title}</div>
                                        <div className="text-slate-500 truncate">{inst.start_time}–{inst.end_time}</div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardContent>
        </Card>
        )}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Upcoming Installations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {installations.filter(i => i.status !== 'completed' && i.status !== 'cancelled').length > 0 ? (
              <div className="space-y-3">
                {installations
                  .filter(i => i.status !== 'completed' && i.status !== 'cancelled')
                  .slice(0, 5)
                  .map((inst) => (
                  <div
                    key={inst.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                    data-testid={`installation-${inst.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: inst.engineer_color + '20' }}
                      >
                        <CalendarIcon className="w-6 h-6" style={{ color: inst.engineer_color }} />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{inst.title}</h4>
                        <p className="text-sm text-slate-500">{inst.customer_name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(inst.scheduled_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {inst.start_time} - {inst.end_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {inst.engineer_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${STATUS_COLORS[inst.status]} border`}>
                        {inst.status.replace('_', ' ')}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(inst)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(inst.id, 'in_progress')}>
                            Start Installation
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(inst.id, 'completed')}>
                            Mark Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(inst.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4" />
                <p>No upcoming installations</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingInstallation ? 'Edit Installation' : 'Schedule Installation'}
              </DialogTitle>
              <DialogDescription>
                {editingInstallation 
                  ? 'Update the installation details'
                  : `Schedule an installation for ${formatDate(selectedDate)}`
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select 
                  value={formData.customer_id} 
                  onValueChange={(v) => setFormData({...formData, customer_id: v})}
                >
                  <SelectTrigger data-testid="select-customer">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} - {c.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assigned Engineers *</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {engineers.map((e) => (
                    <label 
                      key={e.id} 
                      className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={formData.engineer_ids.includes(e.id)}
                        onChange={(ev) => {
                          if (ev.target.checked) {
                            setFormData({...formData, engineer_ids: [...formData.engineer_ids, e.id]});
                          } else {
                            setFormData({...formData, engineer_ids: formData.engineer_ids.filter(id => id !== e.id)});
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: e.color }}
                      />
                      <span className="text-sm">{e.name} ({e.role})</span>
                    </label>
                  ))}
                </div>
                {formData.engineer_ids.length > 0 && (
                  <p className="text-xs text-slate-500">{formData.engineer_ids.length} engineer(s) selected</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., 6.6kW Solar Installation"
                  data-testid="installation-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value, end_date: formData.end_date || e.target.value})}
                    data-testid="installation-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date (Multi-day)</Label>
                  <Input
                    type="date"
                    value={formData.end_date || formData.scheduled_date}
                    min={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    data-testid="installation-end-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.installation_type} 
                  onValueChange={(v) => setFormData({...formData, installation_type: v})}
                >
                  <SelectTrigger data-testid="installation-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTALLATION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    data-testid="start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    data-testid="end-time"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Any special instructions or notes..."
                  data-testid="installation-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="save-installation"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingInstallation ? 'Update' : 'Schedule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Outlook event → CRM Installation dialog */}
        <ImportOutlookEventDialog
          event={importOutlookEvent}
          customers={customers}
          engineers={engineers}
          onClose={() => setImportOutlookEvent(null)}
          onImported={() => { setImportOutlookEvent(null); fetchData(); }}
        />
      </div>
    </Layout>
  );
};
